import { LOCALE_CONFIG, SUPPORTED_LOCALES, type SupportedLocale } from '@erp/shared';
import { Globe2, Loader2, Search } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface LanguageDropdownProps {
  readonly locale: SupportedLocale;
  readonly onLocaleChange: (locale: SupportedLocale) => void | Promise<void>;
  readonly busy?: boolean;
  readonly className?: string;
  readonly compactOnMobile?: boolean;
}

function normalizeSearch(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('en-US')
    .trim();
}

export function LanguageDropdown({
  locale,
  onLocaleChange,
  busy = false,
  className,
  compactOnMobile = false,
}: LanguageDropdownProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const visibleLocales = useMemo(() => {
    const normalizedQuery = normalizeSearch(query);
    if (normalizedQuery === '') return SUPPORTED_LOCALES;
    return SUPPORTED_LOCALES.filter((candidate) =>
      normalizeSearch(
        `${LOCALE_CONFIG[candidate].nativeName} ${t(`language.${candidate}`)} ${candidate}`,
      ).includes(normalizedQuery),
    );
  }, [query, t]);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          window.requestAnimationFrame(() => searchRef.current?.focus());
        } else {
          setQuery('');
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn('min-h-11 gap-2 px-2.5', className)}
          aria-label={`${t('language.label')}: ${LOCALE_CONFIG[locale].nativeName}`}
          aria-busy={busy}
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Globe2 className="h-4 w-4" aria-hidden="true" />
          )}
          <span className={cn(compactOnMobile && 'hidden sm:inline')}>
            {LOCALE_CONFIG[locale].nativeName}
          </span>
          {compactOnMobile ? <span className="uppercase sm:hidden">{locale}</span> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(18rem,calc(100vw-1rem))]">
        <DropdownMenuLabel>{t('language.label')}</DropdownMenuLabel>
        {SUPPORTED_LOCALES.length > 4 ? (
          <div className="relative px-1 pb-1">
            <Search
              className="pointer-events-none absolute start-3 top-2.5 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  const items = searchRef.current
                    ?.closest('[role="menu"]')
                    ?.querySelectorAll<HTMLElement>('[role="menuitemradio"]');
                  const target =
                    event.key === 'ArrowDown'
                      ? items?.item(0)
                      : items?.item((items?.length ?? 1) - 1);
                  target?.focus();
                  return;
                }
                if (event.key !== 'Escape') event.stopPropagation();
              }}
              placeholder={t('language.search')}
              aria-label={t('language.search')}
              className="h-9 ps-8"
            />
          </div>
        ) : null}
        {visibleLocales.length === 0 ? (
          <p className="px-2 py-4 text-center text-sm text-muted-foreground" role="status">
            {t('language.noResults')}
          </p>
        ) : (
          <DropdownMenuRadioGroup
            value={locale}
            onValueChange={(value) => {
              const next = value as SupportedLocale;
              if (next === locale || busy) return;
              void Promise.resolve(onLocaleChange(next)).then(
                () => setOpen(false),
                () => setOpen(false),
              );
            }}
          >
            {visibleLocales.map((candidate) => (
              <DropdownMenuRadioItem
                className="min-h-11"
                key={candidate}
                value={candidate}
                disabled={busy}
              >
                <span className="flex flex-1 items-center justify-between gap-3">
                  <span lang={candidate}>{LOCALE_CONFIG[candidate].nativeName}</span>
                  <span className="text-xs uppercase text-muted-foreground">{candidate}</span>
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
